const LOCAL_STORAGE_KEY = 'audioEffectsSettings';

const defaultState = {
  isAudioEffectsDialogOpen: false,
  compressorEnabled: false,
  eqEnabled: false,
  autoMakeupGain: true,
  eq: {
    highShelf: { frequency: 8000, gain: 0, q: 0.7 },
    mid3: { frequency: 4000, gain: 0, q: 1.0 },
    mid2: { frequency: 1000, gain: 0, q: 1.0 },
    mid1: { frequency: 250, gain: 0, q: 1.0 },
    lowShelf: { frequency: 80, gain: 0, q: 0.7 }
  },
  compressor: {
    threshold: -24,
    ratio: 3.5,
    attack: 30,
    release: 100,
    makeupGain: 0
  }
};

// Load settings from localStorage
function loadSettings() {
  try {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        ...defaultState,
        compressorEnabled: parsed.compressorEnabled ?? false,
        eqEnabled: parsed.eqEnabled ?? false,
        autoMakeupGain: parsed.autoMakeupGain ?? true,
        eq: { ...defaultState.eq, ...parsed.eq },
        compressor: { ...defaultState.compressor, ...parsed.compressor }
      };
    }
  } catch (error) {
    console.error('Error loading audio effects settings:', error);
  }
  return JSON.parse(JSON.stringify(defaultState));
}

// Save settings to localStorage
function saveSettings(state) {
  try {
    const toSave = {
      compressorEnabled: state.compressorEnabled,
      eqEnabled: state.eqEnabled,
      autoMakeupGain: state.autoMakeupGain,
      eq: state.eq,
      compressor: state.compressor
    };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(toSave));
  } catch (error) {
    console.error('Error saving audio effects settings:', error);
  }
}

const state = () => loadSettings();

const getters = {
  isAudioEffectsDialogOpen: state => state.isAudioEffectsDialogOpen,

  isCompressorEnabled: state => state.compressorEnabled,

  isEqEnabled: state => state.eqEnabled,

  isAutoMakeupGainEnabled: state => state.autoMakeupGain,

  eqSettings: state => state.eq,

  compressorSettings: state => state.compressor,

  // Check if any effect is enabled
  hasActiveEffects: state => {
    return state.compressorEnabled || state.eqEnabled;
  }
};

const mutations = {
  SET_DIALOG_OPEN(state, isOpen) {
    state.isAudioEffectsDialogOpen = isOpen;
  },

  SET_COMPRESSOR_ENABLED(state, enabled) {
    state.compressorEnabled = enabled;
    saveSettings(state);
  },

  SET_EQ_ENABLED(state, enabled) {
    state.eqEnabled = enabled;
    saveSettings(state);
  },

  SET_AUTO_MAKEUP_GAIN(state, enabled) {
    state.autoMakeupGain = enabled;
    saveSettings(state);
  },

  SET_EQ_BAND(state, { band, property, value }) {
    if (state.eq[band]) {
      state.eq[band][property] = value;
      saveSettings(state);
    }
  },

  SET_COMPRESSOR_PARAM(state, { param, value }) {
    if (param in state.compressor) {
      state.compressor[param] = value;
      saveSettings(state);
    }
  },

  RESET_TO_DEFAULT(state) {
    state.eq = JSON.parse(JSON.stringify(defaultState.eq));
    state.compressor = JSON.parse(JSON.stringify(defaultState.compressor));
    saveSettings(state);
  }
};

const actions = {
  openDialog({ commit }) {
    commit('SET_DIALOG_OPEN', true);
  },

  closeDialog({ commit }) {
    commit('SET_DIALOG_OPEN', false);
  },

  setCompressorEnabled({ commit }, enabled) {
    commit('SET_COMPRESSOR_ENABLED', enabled);
  },

  setEqEnabled({ commit }, enabled) {
    commit('SET_EQ_ENABLED', enabled);
  },

  setAutoMakeupGain({ commit }, enabled) {
    commit('SET_AUTO_MAKEUP_GAIN', enabled);
  },

  updateEqBand({ commit }, payload) {
    commit('SET_EQ_BAND', payload);
  },

  updateCompressorParam({ commit }, payload) {
    commit('SET_COMPRESSOR_PARAM', payload);
  },

  resetToDefault({ commit }) {
    commit('RESET_TO_DEFAULT');
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
